/**
 * Received-side subscription quality policy (the camera-quality slice's receiving half):
 * what resolution each rendered camera tile should ASK the SFU for, and the dev/e2e seam
 * that measures what the viewer ACTUALLY receives.
 *
 * The problem this fixes: livekit-client's adaptive stream sizes every subscription from
 * the attached element's CSS box times a pixel density that DEFAULTS TO 1 (its heuristic
 * only picks 2 when devicePixelRatio > 2). On the machines the camera-quality slice cares
 * about - Retina Macs (DPR 2), Windows laptops at 125-150% scaling (DPR 1.25-1.5) - the
 * dominant slot (~1000 CSS px wide) then requests only the 720p layer and a grid tile
 * (~450 CSS px) requests the 360p layer, so a 1080p publish is NEVER consumed at its top
 * layer anywhere in the room. Two mechanisms close the gap:
 *
 *  1. The Room connects with `adaptiveStream: { pixelDensity: 'screen' }` (RoomPage's
 *     ROOM_OPTIONS): adaptive sizing then works in real device pixels, which alone fixes
 *     the dominant slot, every screen share and the webinar surfaces.
 *  2. Rendered camera tiles ALSO get explicit desired dimensions per {@link
 *     tileSubscriptionDimensions}: the dominant/spotlight slot requests its full device-pixel
 *     box (never capped - the stage is why people join), and rest-grid tiles are capped so
 *     the decode budget stays sane: at most 8 visible grid tiles ride the medium (720p)
 *     layer ceiling, larger grids drop the ceiling to the low (360p) layer. Offscreen tiles
 *     stay on adaptiveStream's own pause (visibility) - no client-side last-N beyond that.
 *
 * The explicit dimensions are a CEILING, not a floor: livekit-client takes the SMALLER of
 * the adaptive (element) dimensions and the requested ones, so a small window still
 * downgrades gracefully, and the SFU's bandwidth estimator remains free to walk any
 * subscriber further down (that path is the one-step-downgrade safety of the publish
 * ladder, not a product policy).
 */

/** A desired subscription size in device pixels (UpdateTrackSettings width/height). */
export interface DesiredDimensions {
  readonly width: number;
  readonly height: number;
}

/**
 * The role a rendered camera tile plays in the meeting layout. 'stage' is the dominant
 * slot (and the 1:1 remote) - uncapped; 'grid' is a rest-grid tile - capped by
 * {@link gridTileCap}.
 */
export type TileQualityRole = 'stage' | 'grid';

/** Rest-grid tile count at or below which tiles may request up to the medium (720p) layer. */
export const GRID_MEDIUM_MAX_TILES = 8;

/** The medium-layer ceiling for grid tiles: matches the publish ladder's 720p layer. */
export const GRID_TILE_CAP_MEDIUM: DesiredDimensions = { width: 1280, height: 720 };

/** The low-layer ceiling for larger grids: matches the publish ladder's 360p layer. */
export const GRID_TILE_CAP_LOW: DesiredDimensions = { width: 640, height: 360 };

/**
 * The layer ceiling for rest-grid tiles: up to {@link GRID_MEDIUM_MAX_TILES} visible tiles
 * the ceiling is the medium (720p) layer - a handful of 720p decodes and their bitrates fit
 * a laptop and a home downlink; beyond that the ceiling drops to the low (360p) layer so a
 * full 20-40-participant grid does not melt decoders (the SFU's estimator then trims to
 * what the link actually carries). The dominant slot never passes through here.
 */
export function gridTileCap(gridTileCount: number): DesiredDimensions {
  return gridTileCount <= GRID_MEDIUM_MAX_TILES ? GRID_TILE_CAP_MEDIUM : GRID_TILE_CAP_LOW;
}

/**
 * The dimensions a rendered camera tile should request, pure. `cssWidth`/`cssHeight` are
 * the tile's rendered CSS box, `dpr` the browser's devicePixelRatio (>= 1): the desired
 * size is the device-pixel box (ceil), and for grid tiles clamped to the tile-count cap.
 * The SFU forwards the smallest published layer that covers the request, so this is both
 * a sharpness floor (never smaller than the rendered box) and, for grid tiles, a ceiling.
 */
export function tileSubscriptionDimensions(
  role: TileQualityRole,
  cssWidth: number,
  cssHeight: number,
  dpr: number,
  gridTileCount: number,
): DesiredDimensions {
  const density = dpr >= 1 ? dpr : 1;
  const desired: DesiredDimensions = {
    width: Math.ceil(cssWidth * density),
    height: Math.ceil(cssHeight * density),
  };
  if (role === 'stage') {
    return desired;
  }
  const cap = gridTileCap(gridTileCount);
  return {
    width: Math.min(desired.width, cap.width),
    height: Math.min(desired.height, cap.height),
  };
}

/** One subscriber-side receiver stat row for the dev/e2e bridge (numbers only). */
export interface ReceiverVideoStatSnapshot {
  readonly identity: string;
  readonly source: 'camera' | 'screen_share';
  readonly frameWidth: number;
  readonly frameHeight: number;
  /** Cumulative decoded frame count: the harness derives fps from two samples' deltas. */
  readonly framesDecoded: number;
  /** Cumulative received bytes: the harness derives bitrate from two samples' deltas. */
  readonly bytesReceived: number;
  readonly timestamp: number;
}

/**
 * Dev-only e2e seam: exposes EVERY subscribed remote video track's LiveKit receiver stats
 * (RemoteVideoTrack.getReceiverStats: decoded frame dimensions, fps, bytes received) to
 * Playwright page evaluation (`window.__vcSubStats()`). This is the RECEIVING half of the
 * quality evidence - the publish side rides `window.__vcCamStats()` in publishQuality.ts -
 * and the regression pin for the subscription ladder. Stripped from production builds by
 * the import.meta.env.DEV gate, like every other test bridge.
 */
export function exposeReceiverStatsForTests(
  read: () => Promise<readonly ReceiverVideoStatSnapshot[]>,
): void {
  if (import.meta.env.DEV !== true) {
    return;
  }
  window.__vcSubStats = (): Promise<readonly ReceiverVideoStatSnapshot[]> => read();
}

declare global {
  interface Window {
    /** Dev/e2e only: every subscribed remote video track's receiver stats. */
    __vcSubStats?: () => Promise<readonly ReceiverVideoStatSnapshot[]>;
  }
}
